import { Link } from 'react-router-dom';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useDocumentHead } from '@/hooks/use-document-head';

const SERVICES = [
  {
    title: 'Residential Cleaning',
    tag: 'HOMES',
    icon: '🏠',
    desc: 'Weekly, bi-weekly, monthly, or one-time deep cleans. Kitchens, bathrooms, living areas, bedrooms — top-to-bottom or customize your clean.',
    features: ['Kitchen deep scrub', 'Bathroom sanitization', 'Floor care & vacuuming', 'Dusting & surface wipe-down'],
    image: '/images/heroes/hero-1-spray.webp',
  },
  {
    title: 'Commercial Cleaning',
    tag: 'OFFICES',
    icon: '🏢',
    desc: 'After-hours office cleaning, lobby maintenance, restroom sanitation, and breakroom upkeep. Keeping workplaces razor-sharp.',
    features: ['After-hours crews', 'Restroom & breakroom care', 'Floor care & carpet', 'Trash & recycling'],
    image: '/images/heroes/hero-6-skyline-office.webp',
  },
  {
    title: 'Deep Clean',
    tag: 'RESET',
    icon: '⚡',
    desc: 'The full reset. Ceiling fans, baseboards, behind appliances, inside cabinets. For when regular cleaning isn\'t enough.',
    features: ['Inside oven & fridge', 'Baseboard & ceiling fans', 'Window sills & tracks', 'Behind & under furniture'],
    image: '/images/heroes/hero-8-closeup-wipe.webp',
  },
  {
    title: 'Move-In/Out Cleaning',
    tag: 'HANDOVER',
    icon: '📦',
    desc: 'Deposit-saving, buyer-impressing, zero-stress moves. We handle the mess so you can focus on the move.',
    features: ['Full property detail', 'Appliance cleaning', 'Cabinet wipe-down', 'Window & blind cleaning'],
    image: '/images/heroes/hero-3-before-after.webp',
  },
  {
    title: 'Post-Construction',
    tag: 'DEBRIS',
    icon: '🔨',
    desc: 'Dust, drywall residue, paint splatter — gone. Reveal day ready. We make the builder\'s vision shine.',
    features: ['Construction dust removal', 'Surface polishing', 'Debris & waste clearing', 'Final inspection prep'],
    image: '/images/heroes/hero-4-thumbs-up.webp',
  },
  {
    title: 'Medical / Clinical',
    tag: 'CLINICAL',
    icon: '⚕️',
    desc: 'Healthcare-grade sanitation for serious spaces. OSHA-aware protocols, EPA-registered disinfectants, and strict cross-contamination prevention.',
    features: ['EPA-registered disinfectants', 'Cross-contamination protocol', 'Waiting room & exam rooms', 'HIPAA-aware handling'],
    image: '/images/heroes/hero-9-medical-office.webp',
  },
];

export default function ServicesPage() {
  useDocumentHead(
    'House & Commercial Cleaning Services | SOTSVC',
    'Residential, commercial, deep, and move-in/out cleaning from Sonz of Thunder Services across Orlando & Central Florida.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] clip-diag-bottom overflow-hidden">
        <img src="/images/heroes/hero-1-spray.webp" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.9) 0%, rgba(15,15,30,.6) 60%, rgba(0,180,216,.2) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// THE ARSENAL</span>
            </div>
            <h1 className="font-display font-bold text-white text-4xl sm:text-6xl md:text-8xl leading-[0.88]">
              OUR <span className="text-thunder">SERVICES</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-xl mt-6 font-light">
              Six service lines. One standard: perfection.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      {SERVICES.map((service, i) => (
        <section
          key={service.title}
          className={`relative py-24 overflow-hidden ${i % 2 === 0 ? 'bg-charcoal-deep' : 'bg-charcoal'}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`grid lg:grid-cols-2 gap-14 items-center ${i % 2 !== 0 ? 'lg:[direction:rtl]' : ''}`}>
              <ScrollReveal direction={i % 2 === 0 ? 'left' : 'right'} className="relative lg:[direction:ltr]">
                <div className="absolute -inset-4 bg-electric/10 blur-3xl" />
                <div className="relative overflow-hidden border-2 border-electric/40">
                  <img src={service.image} alt={service.title} className="w-full aspect-[4/3] object-cover" />
                  <div className="absolute top-4 left-4 bg-thunder text-charcoal-deep font-display font-bold px-4 py-2 tracking-[0.2em] text-xs">
                    {service.tag}
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal direction={i % 2 === 0 ? 'right' : 'left'} className="lg:[direction:ltr]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-[2px] w-10 bg-electric" />
                  <span className="text-electric font-display tracking-[0.4em] text-xs">
                    // {String(i + 1).padStart(2, '0')} / 06
                  </span>
                </div>
                <div className="text-5xl mb-4">{service.icon}</div>
                <h2 className="font-display font-bold text-white text-4xl sm:text-5xl leading-[0.9] mb-6">
                  {service.title.toUpperCase()}
                </h2>
                <p className="text-white/70 text-lg leading-relaxed mb-8">{service.desc}</p>
                <ul className="space-y-3 mb-10">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-center gap-4 text-white/80 font-display tracking-wider text-sm">
                      <span className="text-electric text-xl">&#x25B8;</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
                  className="bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-8 py-4 tracking-[0.25em] text-sm transition"
                >
                  GET A QUOTE →
                </button>
              </ScrollReveal>
            </div>
          </div>
        </section>
      ))}

      {/* Process */}
      <section className="relative py-28 bg-charcoal-deep border-y-2 border-electric/30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-electric font-display tracking-[0.4em] text-xs mb-3">// HOW IT WORKS</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              THE <span className="text-thunder">PROCESS</span>
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'CALL OR QUOTE', desc: 'Reach out by phone or form. We respond within one hour.' },
              { step: '02', title: 'WALKTHROUGH', desc: 'We assess your space in person. No surprises, no hidden fees.' },
              { step: '03', title: 'WE CLEAN', desc: 'Our crew arrives on time, in uniform, with everything needed.' },
              { step: '04', title: 'INSPECTION', desc: 'You inspect. If it\'s not right, we come back — guaranteed.' },
            ].map((p, i) => (
              <ScrollReveal key={p.step} delay={i * 100}>
                <div className="border-t-4 border-electric p-8 bg-charcoal-mid/30 h-full">
                  <div className="font-display font-bold text-electric text-6xl mb-4" style={{ textShadow: '0 0 30px rgba(0,180,216,.3)' }}>
                    {p.step}
                  </div>
                  <h3 className="font-display font-bold text-white text-xl mb-3 tracking-wider">{p.title}</h3>
                  <p className="text-white/60 leading-relaxed">{p.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <ScrollReveal className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
            READY TO <span className="text-thunder">HIT HARD?</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric"
            >
              GET FREE QUOTE →
            </button>
            <a
              href="tel:4074616039"
              className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition"
            >
              CALL (407) 461-6039
            </a>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
